import { createContext, useContext, useMemo, useState, type ReactNode } from "react"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

interface AmbientMotion {
  /** True while decorative motion (BitBoy, the top sky) should hold still. */
  readonly paused: boolean
  readonly setPaused: (paused: boolean) => void
}

const AmbientMotionContext = createContext<AmbientMotion | null>(null)

/**
 * One pause switch for every decorative animation on the page. It follows
 * the reduced-motion preference until the visitor presses a pause control,
 * and every control reflects the same state.
 */
export function AmbientMotionProvider({ children }: { readonly children: ReactNode }) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [userPaused, setUserPaused] = useState<boolean | null>(null)

  const value = useMemo(
    (): AmbientMotion => ({
      paused: userPaused ?? prefersReducedMotion,
      setPaused: setUserPaused,
    }),
    [userPaused, prefersReducedMotion]
  )

  return <AmbientMotionContext.Provider value={value}>{children}</AmbientMotionContext.Provider>
}

export function useAmbientMotion(): AmbientMotion {
  const value = useContext(AmbientMotionContext)
  if (!value) throw new Error("useAmbientMotion must be used inside AmbientMotionProvider")
  return value
}
